import apiClient from "@/lib/api";

const RECRUITMENT_BASE_PATH = "/admin/recruitment";

export const getRecruitmentStats = async () => {
  const response = await apiClient.get(`${RECRUITMENT_BASE_PATH}/stats`);
  return response.data;
};

const CV_BUILDER_BASE_PATH = "/recruitment/cv-builder";

const preserveBinaryResponseError = async (error) => {
  const data = error?.response?.data;
  if (data instanceof Blob && data.type?.includes("application/json")) {
    try {
      error.response.data = JSON.parse(await data.text());
    } catch {
      // Keep the original binary body when the server response is not JSON.
    }
  }
  throw error;
};

export const getAllCVDrafts = async () => {
  const response = await apiClient.get(`${CV_BUILDER_BASE_PATH}/drafts`);
  return response.data;
};

export const getCVDraftById = async (id) => {
  const response = await apiClient.get(`${CV_BUILDER_BASE_PATH}/drafts/${id}`);
  return response.data;
};

export const createCVDraft = async (draftData, duplicateAction = null) => {
  const params = duplicateAction ? { duplicate_action: duplicateAction } : {};
  const response = await apiClient.post(`${CV_BUILDER_BASE_PATH}/drafts`, draftData, { params });
  return response.data;
};

export const updateCVDraft = async (id, draftData, expectedRevision = null) => {
  const params = Number.isInteger(expectedRevision) ? { expected_revision: expectedRevision } : {};
  const response = await apiClient.put(`${CV_BUILDER_BASE_PATH}/drafts/${id}`, draftData, { params });
  return response.data;
};

export const validateCVDraftForExport = async (id) => {
  const response = await apiClient.post(`${CV_BUILDER_BASE_PATH}/drafts/${id}/validate`);
  return response.data;
};

export const deleteCVDraft = async (id) => {
  const response = await apiClient.delete(`${CV_BUILDER_BASE_PATH}/drafts/${id}`);
  return response.data;
};

export const generateCVFromDraft = async (id, format = "pdf") => {
  try {
    const response = await apiClient.post(
      `${CV_BUILDER_BASE_PATH}/drafts/${id}/generate`,
      {},
      {
        params: { format },
        responseType: "blob",
      },
    );
    return response.data;
  } catch (error) {
    return preserveBinaryResponseError(error);
  }
};

export const generateCVPDFFromDraft = async (id) => {
  return generateCVFromDraft(id, "pdf");
};

export const generateCVDOCXFromDraft = async (id) => {
  return generateCVFromDraft(id, "docx");
};

export const previewCVPDFFromDraft = async (id) => {
  try {
    const response = await apiClient.post(`${CV_BUILDER_BASE_PATH}/drafts/${id}/preview`, {}, {
      responseType: "blob",
    });
    if (response.data && typeof response.data === "object") {
      response.data.headers = response.headers;
    }
    return response.data;
  } catch (error) {
    return preserveBinaryResponseError(error);
  }
};

export const previewCVDraftPayload = async (draftData) => {
  try {
    const response = await apiClient.post(`${CV_BUILDER_BASE_PATH}/preview`, draftData, {
      responseType: "blob",
      headers: {
        "Accept": "application/pdf",
        "Content-Type": "application/json",
      },
    });
    if (response.data && typeof response.data === "object") {
      response.data.headers = response.headers;
    }
    return response.data;
  } catch (error) {
    return preserveBinaryResponseError(error);
  }
};

export const duplicateCVDraft = async (id) => {
  const response = await apiClient.post(`${CV_BUILDER_BASE_PATH}/drafts/${id}/duplicate`);
  return response.data;
};

export const parseCv = async (file, forceRefresh = false, onProgress = null) => {
  if (typeof onProgress === "function") {
    try {
      const job = await createParseJob(file, forceRefresh);
      if (job?.job_id) {
        if (job.progress) onProgress(job.progress);
        const maxTicks = 120;
        for (let i = 0; i < maxTicks; i++) {
          await new Promise((r) => setTimeout(r, 1000));
          const status = await getParseJobStatus(job.job_id);
          if (status?.progress) onProgress(status.progress);
          if (status.status === "completed") {
            return status.result;
          }
          if (status.status === "failed") {
            const err = status.error || { success: false, error_message: "Échec de l'import" };
            return err;
          }
        }
      }
    } catch {
      // Fallback to synchronous parse endpoint if parse-jobs is unavailable
    }
  }

  const formData = new FormData();
  formData.append("file", file);
  const params = forceRefresh ? { force_refresh: true } : {};
  const response = await apiClient.post(`${CV_BUILDER_BASE_PATH}/parse`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    params,
    timeout: 60000,
  });
  return response.data;
};

export const createParseJob = async (file, forceRefresh = false, duplicateAction = null) => {
  const formData = new FormData();
  formData.append("file", file);
  const params = {};
  if (forceRefresh) params.force_refresh = true;
  if (duplicateAction) params.duplicate_action = duplicateAction;
  
  const response = await apiClient.post(`${CV_BUILDER_BASE_PATH}/parse-jobs`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    params,
    timeout: 30000,
  });
  return response.data;
};

export const getParseJobStatus = async (jobId) => {
  const response = await apiClient.get(`${CV_BUILDER_BASE_PATH}/parse-jobs/${jobId}`);
  return response.data;
};

export const deleteCVCache = async (cvHash) => {
  const response = await apiClient.delete(`${CV_BUILDER_BASE_PATH}/cv-cache/by-hash/${cvHash}`);
  return response.data;
};

// ---------------------------------------------------------------------------
// Recruitment Workflow V2 (L0-L8)
// ---------------------------------------------------------------------------

export const getRecruitmentWorkflowDashboardStats = async () => {
  const response = await apiClient.get("/recruitment/dashboard/stats");
  return response.data;
};

export const listApplications = async (params = {}) => {
  const response = await apiClient.get("/recruitment/applications", { params });
  return response.data;
};

export const getApplication = async (applicationId) => {
  const response = await apiClient.get(`/recruitment/applications/${applicationId}`);
  return response.data;
};

export const createApplication = async (data) => {
  const response = await apiClient.post("/recruitment/applications", data);
  return response.data;
};

export const advanceApplicationStage = async (applicationId, notes = null) => {
  const response = await apiClient.post(`/recruitment/applications/${applicationId}/advance`, { notes });
  return response.data;
};

export const backApplicationStage = async (applicationId, reason = null) => {
  const response = await apiClient.post(`/recruitment/applications/${applicationId}/back`, { reason });
  return response.data;
};

export const validateApplicationTech = async (applicationId, approved, comment = null) => {
  const response = await apiClient.post(`/recruitment/applications/${applicationId}/validate-tech`, {
    approved,
    comment,
  });
  return response.data;
};

export const validateApplicationCommercial = async (applicationId, approved, comment = null) => {
  const response = await apiClient.post(`/recruitment/applications/${applicationId}/validate-commercial`, {
    approved,
    comment,
  });
  return response.data;
};

export const decideApplication = async (applicationId, outcome, rejectionSource = null, reason = null) => {
  const response = await apiClient.post(`/recruitment/applications/${applicationId}/decide`, {
    outcome,
    rejection_source: rejectionSource,
    reason,
  });
  return response.data;
};

export const transferApplicationOwner = async (applicationId, newOwner) => {
  const response = await apiClient.put(`/recruitment/applications/${applicationId}/owner`, newOwner);
  return response.data;
};

export const extractNeedRequirements = async (text, existingTitle = null) => {
  const payload = { text };
  if (existingTitle && typeof existingTitle === "string" && existingTitle.trim()) {
    payload.existing_title = existingTitle.trim();
  }
  const response = await apiClient.post("/recruitment/needs/extract-requirements", payload);
  return response.data;
};

export const updateNeed = async (needId, data) => {
  const response = await apiClient.put(`/recruitment/needs/${needId}`, data);
  return response.data;
};

export const listCommunications = async (params = {}) => {
  const response = await apiClient.get("/recruitment/communications", { params });
  return response.data;
};

export const listInternalNotifications = async (isRead = null, limit = null) => {
  const params = {};
  if (isRead !== null && isRead !== undefined) params.is_read = isRead;
  if (limit !== null && limit !== undefined) params.limit = limit;
  const response = await apiClient.get("/recruitment/notifications", { params });
  return response.data;
};

export const markInternalNotificationRead = async (notificationId) => {
  const response = await apiClient.post(`/recruitment/notifications/${notificationId}/read`);
  return response.data;
};

// CV Cycles Workflow API
export const getCandidateCvCycles = async (candidateId) => {
  const response = await apiClient.get(`/recruitment/candidates/${candidateId}/cv-cycles`);
  return response.data;
};

export const getCvCycle = async (cycleId) => {
  const response = await apiClient.get(`/recruitment/cv-cycles/${cycleId}`);
  return response.data;
};

export const getCvCycleInvitations = async (cycleId) => {
  const response = await apiClient.get(`/recruitment/cv-cycles/${cycleId}/invitations`);
  return response.data;
};

export const getCvCycleHistory = async (cycleId) => {
  const response = await apiClient.get(`/recruitment/cv-cycles/${cycleId}/history`);
  return response.data;
};

export const inviteCandidateCvCycle = async (cycleId, payload) => {
  const response = await apiClient.post(`/recruitment/cv-cycles/${cycleId}/invite`, payload);
  return response.data;
};

export const revokeCvCycleInvitation = async (cycleId, invitationId) => {
  const response = await apiClient.post(`/recruitment/cv-cycles/${cycleId}/invitations/${invitationId}/revoke`);
  return response.data;
};

export const finalizeCvCycle = async (cycleId) => {
  const response = await apiClient.post(`/recruitment/cv-cycles/${cycleId}/finalize`);
  return response.data;
};

export const createApplicationPresentation = async (applicationId, payload) => {
  const response = await apiClient.post(`/recruitment/applications/${applicationId}/presentations`, payload);
  return response.data;
};

// Interviews Workflow API
export const listApplicationInterviews = async (applicationId) => {
  const response = await apiClient.get(`/recruitment/applications/${applicationId}/interviews`);
  return response.data;
};

export const createApplicationInterview = async (applicationId, payload) => {
  const response = await apiClient.post(`/recruitment/applications/${applicationId}/interviews`, payload);
  return response.data;
};

export const declineInterview = async (interviewId, payload) => {
  const response = await apiClient.post(`/recruitment/interviews/${interviewId}/decline`, payload);
  return response.data;
};

export const submitInterviewFeedback = async (interviewId, payload) => {
  const response = await apiClient.post(`/recruitment/interviews/${interviewId}/feedback`, payload);
  return response.data;
};

// Communications Workflow API
export const previewCommunication = async (payload) => {
  const response = await apiClient.post("/recruitment/communications/preview", payload);
  return response.data;
};

export const createCommunication = async (payload) => {
  const response = await apiClient.post("/recruitment/communications", payload);
  return response.data;
};

export const sendCommunication = async (communicationId) => {
  const response = await apiClient.post(`/recruitment/communications/${communicationId}/send`);
  return response.data;
};

export const getCandidatePoolStatus = async (candidateId) => {
  const response = await apiClient.get(`/recruitment/candidates/${candidateId}/pool`);
  return response.data;
};

export const updateCandidatePoolStatus = async (candidateId, status, reason = null) => {
  const response = await apiClient.put(`/recruitment/candidates/${candidateId}/pool`, {
    status,
    reason,
  });
  return response.data;
};

export const generateMergeToken = async (targetCandidateId, sourceCandidateId) => {
  const response = await apiClient.post(`/recruitment/candidates/${targetCandidateId}/merge-token`, {
    source_candidate_id: sourceCandidateId,
  });
  return response.data;
};

export const mergeCandidates = async (targetCandidateId, mergePayload) => {
  const response = await apiClient.post(`/recruitment/candidates/${targetCandidateId}/merge`, mergePayload);
  return response.data;
};

export const detachApplication = async (applicationId, reason = null, extra = {}) => {
  const response = await apiClient.post(`/recruitment/applications/${applicationId}/detach`, {
    reason,
    ...extra,
  });
  return response.data;
};

export const detachCandidateFromNeed = async (needId, candidateId, reason = null) => {
  const response = await apiClient.delete(`/recruitment/needs/${needId}/candidates/${candidateId}`, {
    data: { reason },
  });
  return response.data;
};

export const deleteCandidate = async (candidateId, reason = null, force = false) => {
  const response = await apiClient.delete(`/recruitment/candidates/${candidateId}`, {
    data: { reason, force },
  });
  return response.data;
};

export const recruitmentApi = {
  getRecruitmentStats,
  getAllCVDrafts,
  getCVDraftById,
  createCVDraft,
  updateCVDraft,
  validateCVDraftForExport,
  deleteCVDraft,
  generateCVFromDraft,
  generateCVPDFFromDraft,
  generateCVDOCXFromDraft,
  previewCVPDFFromDraft,
  previewCVDraftPayload,
  duplicateCVDraft,
  parseCv,
  createParseJob,
  getParseJobStatus,
  deleteCVCache,
  getRecruitmentWorkflowDashboardStats,
  listApplications,
  getApplication,
  createApplication,
  advanceApplicationStage,
  backApplicationStage,
  validateApplicationTech,
  validateApplicationCommercial,
  decideApplication,
  transferApplicationOwner,
  extractNeedRequirements,
  updateNeed,
  listCommunications,
  previewCommunication,
  createCommunication,
  sendCommunication,
  listInternalNotifications,
  markInternalNotificationRead,
  getCandidateCvCycles,
  getCvCycle,
  getCvCycleInvitations,
  getCvCycleHistory,
  inviteCandidateCvCycle,
  revokeCvCycleInvitation,
  finalizeCvCycle,
  createApplicationPresentation,
  listApplicationInterviews,
  createApplicationInterview,
  declineInterview,
  submitInterviewFeedback,
  getCandidatePoolStatus,
  updateCandidatePoolStatus,
  generateMergeToken,
  mergeCandidates,
  detachApplication,
  detachCandidateFromNeed,
  deleteCandidate,
};

export default recruitmentApi;


export async function downloadOriginalCV(id) {
  const response = await apiClient.get(`${CV_BUILDER_BASE_PATH}/drafts/${id}/original`, { responseType:'blob' });
  const url = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  const disposition = response.headers?.['content-disposition'] || '';
  const name = disposition.match(/filename\*=UTF-8''(.+)$/i)?.[1];
  link.download = name ? decodeURIComponent(name) : 'CV_original';
  link.click();
  setTimeout(() => URL.revokeObjectURL(url),0);
}
